/**
 * @file   src\components\Onboarding\Dashboard\LeftpanelTabContentRight.js
 * @brief  Match tab left panel - property selector + demand insights
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useState, useContext } from "react";
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { filterContext } from "../../../pages/v2/Home";
import { USER_TYPE_AGENT } from "../../../constants/common";
import {
  getLocalStorage,
  setLocalStorage,
  removeLocalStorage,
  decodeParam,
} from "../../../helpers/common";
import { USER_INFO_KEY } from "../../../constants/common";
import { usePrevious } from "../../../hooks/usePrevious";
import { getMatchClientList } from "../../../services/agentClientsService";
import { renameKey, combineKey } from "../../../helpers/common";
import { getInventoryListByUserId } from "../../../services/inventoryService";
import { fetchUserDetails } from "../../../actions/userprofile";
import { useSearchParams } from "react-router-dom";
import AddProperty from "./AddProperty";
import PropertySelectorDropdown from "./PropertySelectorDropdown";
import MatchDemandPanel from "./MatchDemandPanel";

const LeftpanelTabContentRight = (props) => {
  const dispatch = useDispatch();
  const filterCntxt = useContext(filterContext);
  const [searchParams] = useSearchParams();
  const invIdfromUrl = searchParams.get("InvId");
  const clientIdFromUrl = searchParams.get("ClientId");

  const decodedInvId = invIdfromUrl ? decodeParam(invIdfromUrl) : null;
  const decodedClientId = clientIdFromUrl ? decodeParam(clientIdFromUrl) : null;

  const previouslySelectedClient = getLocalStorage("MatchSelectedClient");
  const previouslySelectedProperty = getLocalStorage("selectedPropertyId");
  // Match tab demand insights - read match data from filterContext
  const { adType, adListGitchaGenie, totalCountMatch, setMatchPriceFilter } = filterCntxt;
  const sessionData = getLocalStorage(USER_INFO_KEY);
  const userType = _.get(sessionData, "userInfo.userType", "");
  const userId = _.get(sessionData, "userInfo.userId", "");
  const [propertyList, setPropertyList] = useState(null);
  const [clientList, setClientList] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );
  const [userDetails, setUserDetails] = useState(null);
  const { updateFilterGitchaGenie, selectedProperty, setSelectedProperty } =
    props;
  const key = getLocalStorage("FilterTabKey");
  const [isPremiumUser, setIsPremiumUser] = useState(null);

  const prevAdType = usePrevious(adType);
  const prevInventoryId = usePrevious(selectedProperty?.Inv_Id);

  const [reload, setReload] = useState(0);

  useEffect(() => {
    getClientList();
    // Load inventory on mount without client filtering (isPremiumUser may still be null).
    // Re-fetched with proper filtering once isPremiumUser resolves via the separate useEffect below.
    if (userId) {
      getInventoryList(true);
      dispatch(fetchUserDetails());
    }
  }, [key, adType, reload]);

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      setUserDetails(userDetailsFromStore[0]);
      if (userDetailsFromStore[0]?.period !== 1 && userType === 2) {
        setIsPremiumUser(true);
        setLocalStorage("isPremiumUser", 2); // 2 for premium
      } else {
        setIsPremiumUser(false);
        setLocalStorage("isPremiumUser", 1); // 1 for non premium
      }
    } else {
      setUserDetails(null);
    }
  }, [userDetailsFromStore]);

  const updateReloadParam = () => {
    setReload(reload + 1);
  };

  // Re-fetch inventory once isPremiumUser resolves (skip client filter -
  // Match tab property selector should always show all agent inventory)
  useEffect(() => {
    if (isPremiumUser !== null && userId) {
      getInventoryList(true);
    }
  }, [isPremiumUser]);

  useEffect(() => {
    if (selectedClient) {
      setLocalStorage("MatchSelectedClient", selectedClient);
      // Reload inventory when adType changes (skip client filter -
      // Match tab property selector shows all inventory regardless of client)
      getInventoryList(true);
    }
  }, [adType, selectedClient]);

  useEffect(() => {
    if (clientList && clientList?.length > 0) {
      if (decodedClientId) {
        const clientFromUrl = clientList?.find(
          (client) => client.value == decodedClientId,
        );
        if (clientFromUrl) {
          setSelectedClient(clientFromUrl);
        } else {
          setSelectedClient(clientList[0]);
        }
      } else if (previouslySelectedClient) {
        const previousClient = clientList?.find(
          (client) => client.value == previouslySelectedClient.value,
        );
        if (previousClient) {
          setSelectedClient(previousClient);
        } else {
          setSelectedClient(clientList[0]);
        }
      } else {
        setSelectedClient(clientList[0]);
      }
    }
  }, [clientList, decodedClientId]);

  useEffect(() => {
    if (prevInventoryId != selectedProperty?.Inv_Id || adType != prevAdType) {
      setLocalStorage("selectedPropertyId", selectedProperty);
      updateFilterGitchaGenie({
        inventoryId: selectedProperty?.Inv_Id,
        city: selectedProperty?.City,
        state: selectedProperty?.State,
      });
    }
  }, [selectedProperty]);

  useEffect(() => {
    if (propertyList && propertyList.length > 0) {
      if (decodedInvId != null) {
        const invFromUrl = propertyList.find(
          (property) => property.Inv_Id == decodedInvId,
        );
        if (invFromUrl) {
          setSelectedProperty(invFromUrl);
        } else {
          setSelectedProperty(propertyList[0]);
        }
      } else if (previouslySelectedProperty) {
        const previousProperty = propertyList.find(
          (property) => property.Inv_Id == previouslySelectedProperty.Inv_Id,
        );
        if (previousProperty) {
          setSelectedProperty(previousProperty);
        } else {
          setSelectedProperty(propertyList[0]);
        }
      } else {
        setSelectedProperty(propertyList[0]);
      }
    } else {
      removeLocalStorage("selectedPropertyId");
      setSelectedProperty(null);
    }
  }, [propertyList, decodedInvId]);

  /**
   * To fetch inventory details.
   * skipClientFilter=true on initial mount to load ALL properties before premium status resolves.
   * Once premium status and client are known, re-fetches with client filtering.
   * If a client-filtered call returns empty, falls back to unfiltered so the selector stays visible.
   */
  const getInventoryList = async (skipClientFilter = false) => {
    if (userId) {
      const useClientFilter = !skipClientFilter && userType == 2 && isPremiumUser;
      let query;
      if (useClientFilter) {
        query = {
          adType: adType,
          clientId: selectedClient?.value,
        };
      } else {
        query = {
          adType: adType,
        };
      }
      await getInventoryListByUserId(query)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            if (responseResult.length > 0) {
              setPropertyList(responseResult);
            } else if (!useClientFilter) {
              // Only clear the list when the unfiltered query is empty (user truly has no inventory).
              // When a client-filtered query returns empty, keep the existing list so the selector stays visible.
              setPropertyList(null);
            }
          }
        })
        .catch((error) => {
          console.log("Error", error);
        });
    }
  };
  /**
   * To fetch client details for agents
   */
  const getClientList = async () => {
    if (userId && key == "match") {
      await getMatchClientList()
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            responseResult?.ClientList.forEach((obj) =>
              renameKey(obj, "ClientId", "value"),
            );
            responseResult?.ClientList.forEach((obj) =>
              combineKey(obj, "FirstName", "LastName", "label"),
            );
            setClientList(responseResult?.ClientList);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };

  return (
    <>
      <div className="filter-properties-wrap pb-20">
        <h6>My Properties</h6>
        {propertyList ? (
          <PropertySelectorDropdown
            propertyList={propertyList}
            selectedProperty={selectedProperty}
            setSelectedProperty={setSelectedProperty}
            adType={adType}
            userType={userType}
            selectedClient={selectedClient}
            userDetails={userDetails}
            updateReloadParam={updateReloadParam}
          />
        ) : (
          <AddProperty
            isAgent={userType === USER_TYPE_AGENT}
            selectedClient={selectedClient}
            userDetails={userDetails}
          />
        )}
      </div>

      {/* Match tab demand insights - replaces dual-handle price slider */}
      <div className="filter-properties-wrap mt-3 border-bottom-0" data-tour="demand-panel">
        <h6>{userType === USER_TYPE_AGENT ? "Demand Insights" : "Buyer Activity"}</h6>
        <MatchDemandPanel
          selectedProperty={selectedProperty}
          adType={adType}
          totalCountMatch={totalCountMatch}
          adListGitchaGenie={adListGitchaGenie}
          onSliderPriceChange={setMatchPriceFilter}
          isAgent={userType === USER_TYPE_AGENT}
        />
      </div>
    </>
  );
};
export default LeftpanelTabContentRight;
